import React, { useState, useEffect, useRef } from 'react';
import { I18n } from 'react-redux-i18n';
import { connect } from 'react-redux';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PartnerContact from '../PartnerContact';
import StepHelper from '../StepHelper';
import CardMenu from '../CardMenu';
import DeletePartnerDialog from '../DeletePartnerDialog';
import { postStepStatus } from '../../../Containers/Partners/service/partnersApi/actions';
import { deletePartnerConfig } from '../../../Containers/Partners/service/partnersConfig/actions';
import { notify } from '../../../Components/Notifier/services/actions';
import './style.scss';

// Types d'input rendus comme un champ texte HTML (<input type=...>).
const TEXT_TYPES = [
	'string',
	'text',
	'email',
	'number',
	'password',
	'tel',
	'url',
	'search',
	'date',
];

// Le type peut arriver sous forme de tableau (Joi.alternatives -> ex. ['string','number']).
// On retient le 1er type texte connu, sinon 'text' par défaut.
const normalizeType = type => {
	if (Array.isArray(type)) return type.find(t => TEXT_TYPES.includes(t)) || 'text';
	return type;
};

/**
 * Carte partenaire — état "En configuration".
 * Base neuve : composant présentationnel alimenté par /partners/all.
 *
 * Deux modes :
 *   - replié  : logo + footer (compteur d'étape + "Continuer la configuration")
 *   - déplié  : header (n° d'étape + logo) / body (vide pour l'instant) / footer
 *
 * Le footer se colore en rouge (texte blanc) au survol dans les deux modes.
 *
 * @param {Object} props.partner partenaire renvoyé par /partners/all
 */
// Position horizontale de la flèche du helper selon la colonne (3 par ligne),
// pour qu'elle pointe vers la tuile.
const ARROW_LEFT_BY_COLUMN = ['20%', '48%', '75%'];

const CardConfiguration = ({
	partner = {},
	index = 0,
	autoOpen = false,
	postStepStatus,
	deletePartnerConfig,
	notify,
}) => {
	const {
		logo,
		name,
		prefix,
		onboarding,
		partner_contact,
		documentation,
	} = partner;
	// autoOpen : déplie la card au montage (ex. retour redirect OAuth Ubereats).
	const [expanded, setExpanded] = useState(autoOpen);
	const [formData, setFormData] = useState({});
	const [deleteOpen, setDeleteOpen] = useState(false);
	const cardRef = useRef(null);

	const label = (onboarding && onboarding.label) || '';
	const inputs = (onboarding && onboarding.inputs) || [];
	const currentStep = onboarding && onboarding.step;
	const stepHelper = onboarding && onboarding.step_helper;
	// Rien à afficher pour l'étape (ni label, ni input, ni data) → fallback.
	const emptyStep = !label && inputs.length === 0 && !(onboarding && onboarding.data);
	// Le step se valide-t-il par le bouton "Étape suivante" ? L'API l'indique via
	// onboarding.submit (défaut true). Mis à false quand la progression est
	// externe (ex. redirect OAuth Ubereats) → on masque le bouton.
	const canSubmit = !onboarding || onboarding.submit !== false;

	// (Ré)initialise les valeurs du formulaire quand l'étape change.
	useEffect(() => {
		const initial = {};
		inputs.forEach(input => {
			if (input.key && input.value !== undefined && input.value !== null) {
				initial[input.key] = input.value;
			}
		});
		setFormData(initial);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep]);

	// À l'entrée en configuration : scroll pour amener le haut de la tuile en haut
	// de la fenêtre, et fermeture sur tout clic en dehors de la tuile.
	useEffect(() => {
		if (!expanded) return undefined;

		if (cardRef.current) {
			cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}

		const handleClickOutside = event => {
			// Ne pas fermer si le clic vise une popup MUI (dialog d'aide, select…).
			if (
				event.target.closest &&
				event.target.closest(
					'.MuiDialog-root, .MuiPopover-root, .card-menu__popper'
				)
			) {
				return;
			}
			if (cardRef.current && !cardRef.current.contains(event.target)) {
				setExpanded(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [expanded]);

	const title = name || (prefix ? I18n.t(`partner.prefix.${prefix}`) : '');

	const stepNb = onboarding && onboarding.step_nb ? onboarding.step_nb : 0;
	const maxStep = onboarding && onboarding.max_step ? onboarding.max_step : 0;
	const progress = maxStep ? Math.min(100, Math.round((stepNb / maxStep) * 100)) : 0;

	const isInitial = currentStep === 'initial';

	const enterConfiguration = () => setExpanded(true);

	// Step "initial" : "Commencer la configuration" avance directement l'étape
	// (et entre en configuration pour afficher l'étape suivante).
	const startConfiguration = () => {
		setExpanded(true);
		goToNextStep();
	};

	// Tous les inputs requis ont-ils une valeur ? (sinon on masque "Étape suivante")
	// On trim les chaînes pour ne pas valider un champ ne contenant que des espaces.
	const requiredFilled = inputs.every(input => {
		if (!input || !input.required) return true;
		const value = formData[input.key];
		if (value === undefined || value === null) return false;
		if (typeof value === 'string') return value.trim() !== '';
		// Case à cocher requise (ex: acceptation CGV) : doit être cochée.
		if (typeof value === 'boolean') return value === true;
		return true;
	});

	const onInputChange = (key, value) =>
		setFormData(prev => ({ ...prev, [key]: value }));

	const copyToClipboard = value => {
		if (navigator.clipboard && value != null) {
			navigator.clipboard.writeText(value);
			notify(I18n.t('partner.copied'), 'success');
		}
	};

	const goToNextStep = () => {
		// Collecte des valeurs des inputs (clé -> valeur) pour le body du POST.
		const data = {};
		inputs.forEach(input => {
			if (!input.key) return;
			if (input.type === 'hidden') {
				data[input.key] = input.value;
			} else if (formData[input.key] !== undefined) {
				const value = formData[input.key];
				// Trim des chaînes pour ne pas envoyer d'espaces superflus.
				data[input.key] = typeof value === 'string' ? value.trim() : value;
			}
		});
		postStepStatus({ prefix, data });
	};

	// Rendu d'un input selon son type (cf. API onboarding.inputs).
	const renderInput = (input, index) => {
		if (!input || !input.type) return null;

		// Lien (ex: télécharger le guide). Par défaut on navigue dans l'onglet
		// courant ; l'API précise input.target="_blank" quand il faut un nouvel
		// onglet. Le token {origin} est remplacé par l'origine du front (ex.
		// redirect_uri OAuth Ubereats : ...redirect_uri={origin}/redirect/...).
		if (input.type === 'link') {
			const href = (input.url || '')
				.split('{origin}')
				.join(window.location.origin);
			return (
				<a
					key={index}
					className="card-config__cta"
					href={href}
					target={input.target || undefined}
					rel={input.target === '_blank' ? 'noopener noreferrer' : undefined}>
					{input.label}
				</a>
			);
		}

		// Copie dans le presse-papier (ex: copier la clé) : la valeur est affichée
		// (tronquée si trop longue) et c'est la valeur complète qui est copiée.
		if (input.type === 'clipboard-copy') {
			return (
				<div
					key={index}
					className="card-config__copy"
					onClick={() => copyToClipboard(input.value)}>
					<span className="card-config__copy-value" title={input.value}>
						{input.value}
					</span>
					<span className="card-config__copy-hint">
						{I18n.t('partner.copy')}
					</span>
				</div>
			);
		}

		// Liste déroulante — options au format { value, label }
		if (input.type === 'select') {
			const options = Array.isArray(input.options) ? input.options : [];
			return (
				<select
					key={index}
					className="card-config__input card-config__select"
					value={formData[input.key] || ''}
					onChange={e => onInputChange(input.key, e.target.value)}>
					<option value="" disabled>
						{input.label || I18n.t('binding.none')}
					</option>
					{options.map((opt, i) => (
						<option key={i} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			);
		}

		// Champ caché : pas d'affichage (valeur incluse dans data à l'envoi)
		if (input.type === 'hidden') return null;

		// Case à cocher (ex: acceptation des CGV — Joi.boolean requis)
		if (input.type === 'boolean' || input.type === 'checkbox') {
			return (
				<label key={index} className="card-config__checkbox">
					<input
						type="checkbox"
						checked={formData[input.key] === true}
						onChange={e => onInputChange(input.key, e.target.checked)}
					/>
					<span>{input.label}</span>
				</label>
			);
		}

		// Champ texte (types HTML classiques + Joi.alternatives sous forme de tableau)
		const type = normalizeType(input.type);
		if (TEXT_TYPES.includes(type)) {
			// Les champs "password" (ex: config resalys/thais) sont masqués.
			const htmlType =
				type === 'string'
					? /password/i.test(input.key || '')
						? 'password'
						: 'text'
					: type;
			return (
				<input
					key={index}
					className="card-config__input"
					type={htmlType}
					placeholder={input.label || ''}
					value={formData[input.key] || ''}
					onChange={e => onInputChange(input.key, e.target.value)}
				/>
			);
		}

		// Type réellement non géré → rien.
		return null;
	};

	// onClick absent => label masqué (visibility) mais son espace est conservé.
	const renderFooter = (footerLabel, onClick) => (
		<div
			className={`card-config__footer${
				onClick ? '' : ' card-config__footer--static'
			}`}
			onClick={onClick || undefined}>
			<span className="card-config__counter">{`${stepNb}/${maxStep}`}</span>
			<div className="card-config__progress">
				<div
					className="card-config__progress-fill"
					style={{ width: `${progress}%` }}
				/>
			</div>
			<span
				className={`card-config__label${
					onClick ? '' : ' card-config__label--hidden'
				}`}>
				{footerLabel}
			</span>
		</div>
	);

	// Abandon de la configuration en cours : simple confirmation, rien n'est
	// encore en production côté partenaire.
	const menuItems = [
		{
			key: 'delete',
			label: I18n.t('partner.delete.menu_config'),
			icon: DeleteOutlineIcon,
			danger: true,
			onClick: () => setDeleteOpen(true),
		},
	];

	const deleteDialog = (
		<DeletePartnerDialog
			open={deleteOpen}
			partnerName={title}
			onClose={() => setDeleteOpen(false)}
			onConfirm={() => deletePartnerConfig({ prefix })}
		/>
	);

	const renderLogo = className =>
		logo ? (
			<img className={className} src={logo} alt={`Logo ${title}`} />
		) : (
			<span className="card-config__title">{title}</span>
		);

	// --- Mode replié ---
	if (!expanded) {
		return (
			<div className="card-config" ref={cardRef}>
				<div className="card-config__menu">
					<CardMenu items={menuItems} iconClassName="card-config__menu-icon" />
				</div>
				<div className="card-config__logo">{renderLogo()}</div>
				{renderFooter(
					isInitial
						? I18n.t('partner.begin_configuration')
						: I18n.t('partner.continue_configuration'),
					isInitial ? startConfiguration : enterConfiguration
				)}
				{deleteDialog}
			</div>
		);
	}

	// --- Mode déplié ---
	// Wrapper (ref) englobant tuile + helper : le clic sur le helper ne ferme pas
	// la configuration, et le scroll amène le haut de la tuile en haut de fenêtre.
	return (
		<>
			<div className="card-config__overlay" />
			<div className="card-config__expanded-wrap" ref={cardRef}>
				<div className="card-config card-config--expanded">
					<div className="card-config__header">
						<h3 className="card-config__step">
							{`${I18n.t('partner.step.step')} ${stepNb}`}
							<PartnerContact
								name={title}
								contact={partner_contact}
								documentation={documentation}
								iconClassName="card-config__help"
							/>
							<CardMenu
								items={menuItems}
								iconClassName="card-config__help"
							/>
						</h3>
						{renderLogo('card-config__header-logo')}
					</div>

					<div className="card-config__body">
						{emptyStep ? (
							<p className="card-config__label-text">
								{I18n.t('partner.click_to_continue')}
							</p>
						) : (
							<>
								{label ? (
									<p className="card-config__label-text">{label}</p>
								) : null}
								{inputs.map((input, index) => renderInput(input, index))}
							</>
						)}
					</div>

					{renderFooter(
						I18n.t('partner.step.next_step'),
						canSubmit && requiredFilled ? goToNextStep : null
					)}
				</div>

				{stepHelper ? (
					<div className="card-config__helper-slot">
						<span
							className="card-config__helper-arrow"
							style={{ left: ARROW_LEFT_BY_COLUMN[index % 3] }}
						/>
						<StepHelper helper={stepHelper} />
					</div>
				) : null}
				{deleteDialog}
			</div>
		</>
	);
};

const mapDispatchToProps = { postStepStatus, deletePartnerConfig, notify };

export default connect(null, mapDispatchToProps)(CardConfiguration);
